/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, signal,
} from '@angular/core';
import { FeedStoreService } from '../services/feed-store.service';
import { createFailureAwareStore } from '../shared/failure-aware-store';
import { focusNewItem } from '../shared/focus-new-item';

export type ProjectsTab = 'suggested' | 'all' | 'urgent';

export const PROJECTS_TABS: { value: ProjectsTab; label: string }[] = [
  { value: 'suggested', label: 'Suggested' },
  { value: 'all', label: 'All' },
  { value: 'urgent', label: 'Urgent' },
];

const SORT = '-updated_at';

// The /projects board in the 2026 design, routed by UTEST_DESIGN_REFRESH_2026 (see PROJECTS_TOOLS_REDESIGN.html).
// Same rules as the AngularJS board: the tab and country live in the URL, an unknown country is dropped,
// Suggested hides when it's empty and is disabled while a filter is set; picking a filter on Suggested switches to All.
// The OS filter isn't in the URL: the `project` state doesn't declare it.
@Component({
  selector: 'ut-projects-page',
  templateUrl: './projects-page.component.html',
  styleUrls: ['./projects-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProjectsPageComponent implements OnInit, OnDestroy {
  // From the `project` state's resolves. `user` is null when logged out.
  @Input() user: any;
  @Input({ required: true }) headerData: any;
  @Input({ required: true }) projectInfo: any;

  readonly tabs = PROJECTS_TABS;

  tab = signal<ProjectsTab>('suggested');
  os: string | null = null;
  country: string | null = null;
  groups: Record<ProjectsTab, any>;

  private removeTransitionHook: () => void;

  constructor(
    @Inject('ProjectService') private ProjectService: any,
    @Inject('$state') private $state: any,
    @Inject('$transitions') private $transitions: any,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.groups = {
      suggested: this.createStore({ state: 'published', suggested_projects: true }),
    } as Record<ProjectsTab, any>;
    this.track(this.groups.suggested.loadOnce()).then(() => {
      if (this.tab() === 'suggested' && !this.shouldShow('suggested')) this.selectTab('all');
    });
    this.applyParams(this.$state.params);
    // The state doesn't reload on search, so back / forward only change the params. Coming back from a project
    // (the sticky board was kept alive) keeps the tab, filters and loaded pages, as the AngularJS board did,
    // even when the link back has no params.
    this.removeTransitionHook = this.$transitions.onSuccess(
      { to: 'project' },
      (transition) => {
        if (transition.from().name === 'project') this.applyParams(transition.params());
        else this.updateUrl();
        // A project page may have loaded more of this list (for its Next link) while the board was away.
        this.cdr.markForCheck();
      },
    );
  }

  ngOnDestroy(): void {
    this.removeTransitionHook();
  }

  get store(): any {
    return this.groups[this.tab()];
  }

  get hasFilters(): boolean {
    return !!(this.os || this.country);
  }

  get emptyTitle(): string {
    if (this.hasFilters) return 'No projects match these filters';
    if (this.tab() === 'urgent') return 'No urgent projects right now';
    if (this.tab() === 'suggested') return 'No suggested projects right now';
    return 'No projects right now';
  }

  cover(src: string): string {
    return `url('${src}')`;
  }

  shouldShow(tab: ProjectsTab): boolean {
    return tab !== 'suggested' || this.groups.suggested.feedItems.length > 0;
  }

  // Suggested ignores the filters, so it can't be opened while one is set.
  isDisabled(tab: ProjectsTab): boolean {
    return tab === 'suggested' && this.hasFilters;
  }

  selectTab(tab: ProjectsTab): void {
    this.tab.set(tab);
    this.track(this.store.loadOnce());
    this.updateUrl();
  }

  setFilter(type: 'os' | 'country', value: string | null): void {
    this[type] = value || null;
    this.applyFilters();
  }

  // The button leaves the page with the last filter, so focus goes to the first filter, as on the legacy board.
  clearFilters(): void {
    this.os = null;
    this.country = null;
    this.applyFilters();
    setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('ut-filter-select input')?.focus());
  }

  retry(): void {
    this.fetchNext(() => (this.store.feedItems.length ? this.store.loadMore() : this.store.load()));
  }

  get status(): string {
    if (this.store.isLoading) return 'Loading projects';
    if (this.store.failed) return '';
    const count = this.store.feedItems.length;
    return count === 1 ? '1 project shown' : `${count} projects shown`;
  }

  loadMore(): void {
    this.fetchNext(() => this.store.loadMore());
  }

  // Focus follows the list: the first new card when the page loaded, Retry again when it failed.
  private fetchNext(fetch: () => Promise<unknown>): void {
    const { store } = this;
    const next = store.feedItems.length;
    this.track(fetch()).then(() => {
      const item = store.feedItems[next];
      if (store.failed) focusNewItem(this.el.nativeElement, '.alert--error button');
      else if (item && store === this.store) focusNewItem(this.el.nativeElement, `#project_${item.id} > article`);
    });
  }

  // Applies the URL's tab and country unless they're already showing (our own URL updates land here too).
  private applyParams(params: any): void {
    const country = params.country ? this.findCountry(params.country) : null;
    let tab: ProjectsTab = PROJECTS_TABS.some((t) => t.value === params.tab) ? params.tab : 'suggested';
    const suggestedEmpty = !this.groups.suggested.isLoading && !this.groups.suggested.feedItems.length;
    if (tab === 'suggested' && (country || this.os || suggestedEmpty)) tab = 'all';

    const filterChanged = !this.groups.all || country !== this.country;
    if (!filterChanged && tab === this.tab()) return;
    if (filterChanged) {
      this.country = country;
      this.setupFiltered();
    }
    this.selectTab(tab);
  }

  private applyFilters(): void {
    this.setupFiltered();
    if (this.tab() === 'suggested') {
      this.selectTab('all');
    } else {
      this.selectTab(this.tab());
    }
  }

  private setupFiltered(): void {
    const filter: any = { state: 'published' };
    if (this.os) filter.os = this.os;
    if (this.country) filter.country = this.country;
    this.groups.all = this.createStore(filter);
    this.groups.urgent = this.createStore({ ...filter, urgent: true });
  }

  private createStore(filter: object): any {
    return createFailureAwareStore(this.feedStore, (params) => this.ProjectService.feed(params), { filter, sort: SORT });
  }

  private findCountry(country: string): string | null {
    return (this.projectInfo.countries || []).find((c) => c.toUpperCase() === country.toUpperCase()) || null;
  }

  private updateUrl(): void {
    this.$state.go('project', { tab: this.tab(), country: this.country }, { location: 'replace' });
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: Promise<unknown>): Promise<unknown> {
    this.cdr.markForCheck();
    return promise.then(() => this.cdr.markForCheck());
  }
}
